import type { Permission } from "@shared/constants/permissions";
import { useMemo } from "react";

import { useAuth } from "~/components/general";
import { canOpenPath } from "~/constants/routePermissions";
import type { UserType } from "~/types/user";
import {
  canChangeRecord,
  canDeleteRecord,
  canRemoveRecord,
  hasPermission,
} from "~/utils/permissions";

type RecordOwner = Parameters<typeof canChangeRecord>[2];

/**
 * Permission checks for the signed-in user:
 * - `can`: the role holds the permission.
 * - `canChange`: may edit the record (any record, with the permission).
 * - `canDelete`: plus, for anyone but Admins, the record is their own.
 * - `canRemove`: plus, for custom roles, the record is their own (built-in
 *   roles delete any, as their permission says).
 * - `canOpen`: the role may open an app page, so a link to it is worth showing.
 */
const usePermissions = () => {
  const { user } = (useAuth() || {}) as { user?: UserType };

  return useMemo(
    () => ({
      can: (permission: Permission) => hasPermission(user, permission),
      canChange: (permission: Permission, createdBy?: RecordOwner) =>
        canChangeRecord(user, permission, createdBy),
      canDelete: (permission: Permission, createdBy: RecordOwner) =>
        canDeleteRecord(user, permission, createdBy),
      canRemove: (permission: Permission, createdBy: RecordOwner) =>
        canRemoveRecord(user, permission, createdBy),
      canOpen: (pathname: string) => canOpenPath(user, pathname),
    }),
    [user],
  );
};

export default usePermissions;
